import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  School as SchoolIcon,
  EmojiEvents as TrophyIcon,
  EmojiEvents,
  Print,
  Close as CloseIcon,
  GetApp as Download,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';

// Lesson data structure
interface Question {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
}

interface Lesson {
  id: number;
  moduleId: number;
  moduleName: string;
  title: string;
  duration: string;
  content: {
    sections: {
      title: string;
      content: string[];
    }[];
  };
  quiz: Question[];
  keyTakeaways: string[];
}

// All 19 lessons data
const lessons: Lesson[] = [
  // MODULE 1
  {
    id: 1,
    moduleId: 1,
    moduleName: 'Introduction to Business Glossary',
    title: 'What is a Business Glossary?',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'The Problem',
          content: [
            'Meet Sarah and Mike. Sarah works in HR and calls it "Form I-9." Mike works in IT and calls it "Employment Verification Document." They\'re talking about the same thing, but the confusion causes delays, errors, and compliance risks.',
            'This isn\'t just frustrating—it\'s costly:',
            '• $15M/year lost to data inconsistency',
            '• $25K per I-9 violation',
            '• 30% error rates without standardization',
          ],
        },
        {
          title: 'The Solution: Business Glossary',
          content: [
            'A Business Glossary is your organization\'s single source of truth for data definitions. It\'s a centralized repository where every term has one official definition that everyone uses.',
            'Think of it as a dictionary for your business data—but much more powerful.',
            'A Business Glossary includes five key components:',
            '1. Clear Definitions - Plain language explanations',
            '2. Business Context - Why each term matters',
            '3. Real-World Examples - Usage scenarios',
            '4. Relationships - How terms connect',
            '5. Regulatory References - Links to official sources',
          ],
        },
        {
          title: 'The Impact',
          content: [
            'Organizations that implement a Business Glossary see dramatic improvements:',
            '• 80% reduction in data errors',
            '• 60% faster employee onboarding',
            '• 95% improvement in compliance',
            '',
            'Customer Success Story:',
            '"The Business Glossary became our single source of truth. Everyone now speaks the same language when it comes to I-9 compliance."',
            '',
            'Results: Before - 30% error rate, $75K in potential fines',
            'After - 99% compliance, zero audit findings',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What is a Business Glossary?',
        options: [
          'A regular dictionary',
          'A single source of truth for data definitions',
          'A database',
          'A spreadsheet',
        ],
        correctAnswer: 1,
      },
      {
        question: 'What percentage reduction in errors do organizations typically see?',
        options: ['50%', '60%', '70%', '80%'],
        correctAnswer: 3,
      },
      {
        question: 'How much faster is employee onboarding with a Business Glossary?',
        options: ['30%', '40%', '50%', '60%'],
        correctAnswer: 3,
      },
    ],
    keyTakeaways: [
      'Business Glossary = Single source of truth for data definitions',
      'Reduces errors, speeds onboarding, improves compliance',
      'Essential for data governance and regulatory compliance',
      'ROI of 300% in first year for most organizations',
    ],
  },
  {
    id: 2,
    moduleId: 1,
    moduleName: 'Introduction to Business Glossary',
    title: "E-Unify's Business Glossary Features",
    duration: '4 minutes',
    content: {
      sections: [
        {
          title: 'Platform Overview',
          content: [
            'E-Unify is a comprehensive data governance platform built specifically for employment verification and immigration compliance.',
            'The Business Glossary is at the heart of this platform, designed to meet the unique needs of organizations managing I-9 compliance, E-Verify, and work authorization.',
          ],
        },
        {
          title: 'Feature 1: Intuitive Interface',
          content: [
            'Clean and Easy to Navigate:',
            '• Top Navigation Bar - Search, Create, Profile, Notifications',
            '• Left Sidebar - All Terms, Categories, Status Filters, My Terms, Recently Viewed',
            '• Main Content Area - Term cards with grid/list views',
            '',
            'Each Term Card Shows:',
            '• Term name in bold',
            '• Brief definition preview',
            '• Category tag',
            '• Status badge (Draft, Review, Approved)',
            '• Last updated date',
            '• Data steward name',
            '• Quick action buttons',
          ],
        },
        {
          title: 'Feature 2: Rich Term Details',
          content: [
            'Click any term to see comprehensive information:',
            '',
            'Definition Section:',
            '• Clear definition in plain language',
            '• Business context explaining why it matters',
            '• Real-world examples showing usage',
            '• Regulatory references with clickable links',
            '',
            'Metadata Section:',
            '• Synonyms and acronyms',
            '• Category classification',
            '• Data steward and business owner',
            '• Creation and update dates',
            '• Complete version history',
          ],
        },
        {
          title: 'Feature 3: Collaboration Tools',
          content: [
            'E-Unify makes teamwork easy:',
            '',
            '💬 Comments - Add questions or feedback directly on any term',
            '@ @Mentions - Tag colleagues to get their input',
            '🔔 Notifications - Stay informed of changes to terms you follow',
            '✅ Review Requests - Request formal review from data stewards',
          ],
        },
        {
          title: 'Feature 4: Governance Workflow',
          content: [
            'Built-in quality control ensures every definition meets standards:',
            '',
            'Workflow Steps:',
            '1. Create - Anyone can propose a new term',
            '2. Draft - Term is saved, visible only to creator',
            '3. Submit - Creator submits for review when ready',
            '4. Review - Data steward reviews and provides feedback',
            '5. Approve - Data steward approves or requests changes',
            '6. Publish - Approved term is published to all users',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'Which feature allows you to tag a colleague for input?',
        options: ['Comments', '@Mentions', 'Notifications', 'Review Requests'],
        correctAnswer: 1,
      },
      {
        question: 'What does a 🟢 green status indicator mean?',
        options: ['Draft', 'In Review', 'Approved', 'Rejected'],
        correctAnswer: 2,
      },
      {
        question: 'Who can approve term definitions?',
        options: ['All Users', 'Contributors', 'Data Stewards', 'Anyone'],
        correctAnswer: 2,
      },
      {
        question: 'What can you see in the relationship visualization?',
        options: [
          'Only the term name',
          'How terms connect to each other',
          'User permissions',
          'Audit logs',
        ],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Intuitive interface - Easy to navigate and use',
      'Rich term details - Comprehensive information in one place',
      'Collaboration tools - Work together seamlessly',
      'Governance workflow - Quality control built-in',
      'Powerful search - Find what you need quickly',
    ],
  },
  {
    id: 3,
    moduleId: 1,
    moduleName: 'Introduction to Business Glossary',
    title: 'Use Cases and Benefits',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'Case Study 1: ABC Corporation - Form I-9 Compliance',
          content: [
            'The Challenge:',
            'ABC Corporation operates 50 locations across 15 states, processing over 5,000 Form I-9s annually.',
            '',
            'Problems:',
            '• Each location interpreted I-9 requirements differently',
            '• 30% of forms had errors during USCIS audit',
            '• Facing $75,000 in potential fines',
            '• Inconsistent document acceptance across offices',
            '',
            'The Results (6 months):',
            '✅ 99% compliance rate (up from 70%)',
            '✅ Zero audit findings',
            '✅ Consistent processes across all 50 locations',
            '✅ 60% reduction in training time for new HR staff',
            '✅ $75,000 saved in potential fines',
          ],
        },
        {
          title: 'Case Study 2: XYZ Tech - E-Verify Integration',
          content: [
            'The Challenge:',
            'XYZ Tech employs 2,000 people and hires 500 new employees annually. They struggled with E-Verify terminology across multiple systems.',
            '',
            'Problems:',
            '• HR system, payroll, and E-Verify portal used different terms',
            '• 40 hours per month on manual reconciliation',
            '• 15% error rate in E-Verify cases',
            '',
            'The Results (3 months):',
            '✅ Eliminated 40 hours of manual work per month',
            '✅ Reduced E-Verify errors by 85%',
            '✅ Improved case resolution time by 50%',
            '✅ Automated data synchronization',
            '✅ $60,000 saved annually in labor costs',
          ],
        },
        {
          title: 'Case Study 3: DEF Industries - Regulatory Change Management',
          content: [
            'The Scenario:',
            'When USCIS updated Form I-9 requirements in 2023, organizations had 60 days to comply.',
            '',
            'Traditional Approach (Without Business Glossary):',
            '• Update dozens of documents manually',
            '• Email updates to 100 locations',
            '• Train 500 HR staff individually',
            '• Typical timeline: 6-8 weeks',
            '',
            'With E-Unify Business Glossary:',
            '✅ Full compliance in 3 days (vs 6 weeks)',
            '✅ 95% faster implementation',
            '✅ Zero compliance gaps',
            '✅ 100% consistent adoption',
            '✅ Complete change documentation',
          ],
        },
      ],
    },
    quiz: [
      {
        question: "What was ABC Corporation's compliance rate AFTER implementing E-Unify?",
        options: ['70%', '85%', '95%', '99%'],
        correctAnswer: 3,
      },
      {
        question: 'How much time did XYZ Tech save per month?',
        options: ['10 hours', '20 hours', '40 hours', '60 hours'],
        correctAnswer: 2,
      },
      {
        question: 'How long did it take DEF Industries to achieve full compliance?',
        options: ['1 day', '3 days', '1 week', '6 weeks'],
        correctAnswer: 1,
      },
      {
        question: 'What is the average ROI in the first year?',
        options: ['100%', '200%', '300%', '400%'],
        correctAnswer: 2,
      },
      {
        question: 'Which benefit did ALL three companies experience?',
        options: ['Improved compliance', 'New software', 'More employees', 'Larger budgets'],
        correctAnswer: 0,
      },
    ],
    keyTakeaways: [
      'Compliance - Meet regulatory requirements consistently',
      'Efficiency - Eliminate manual reconciliation',
      'Accuracy - Reduce errors by 80-85%',
      'Agility - Respond to changes 95% faster',
      'Cost Savings - Avoid regulatory fines',
      'Average ROI: 300% in first year',
    ],
  },
  // MODULE 2: Navigation
  {
    id: 4,
    moduleId: 2,
    moduleName: 'Navigating the Business Glossary',
    title: 'Interface Overview',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'Dashboard Layout',
          content: [
            'When you open the Business Glossary, you\'ll see a clean, organized layout designed for easy navigation.',
          ],
        },
        {
          title: 'Top Navigation Bar',
          content: [
            'Left Side:',
            '• E-Unify Logo - Click to return to home',
            '• Business Glossary - Current section title',
            '',
            'Center:',
            '• Search Bar - Find any term instantly (keyboard shortcut: /)',
            '',
            'Right Side:',
            '• ➕ Create New Term button',
            '• 👤 Profile Menu - Your account settings',
            '• 🔔 Notifications - Updates and alerts',
          ],
        },
        {
          title: 'Left Sidebar Navigation',
          content: [
            'All Terms - Shows every term in the glossary',
            '',
            'Categories (expandable):',
            '• Immigration Forms (45 terms)',
            '• Employment Authorization (38 terms)',
            '• Verification Processes (28 terms)',
            '• Compliance Terms (14 terms)',
            '',
            'Status Filters:',
            '• 🟡 Draft (15) - Work in progress',
            '• 🔵 In Review (8) - Awaiting approval',
            '• 🟢 Approved (230) - Official definitions',
            '• 🔴 Rejected (2) - Needs revision',
            '• ⚫ Archived (10) - No longer active',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'Where can you find terms you recently viewed?',
        options: ['All Terms', 'My Terms', 'Recently Viewed', 'Categories'],
        correctAnswer: 2,
      },
      {
        question: 'What does the number in parentheses next to a category mean?',
        options: ['Number of users', 'Number of terms in that category', 'Priority level', 'Pending approvals'],
        correctAnswer: 1,
      },
      {
        question: 'Which status badge color indicates an approved term?',
        options: ['Yellow', 'Blue', 'Green', 'Red'],
        correctAnswer: 2,
      },
      {
        question: 'What keyboard shortcut focuses the search bar?',
        options: ['Ctrl + F', '/ (forward slash)', 'Ctrl + S', 'Tab'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Top navigation - Search, create, profile, notifications',
      'Left sidebar - Categories, filters, my terms, recent',
      'Main area - Term cards with grid/list views',
      'Term cards - Name, status, preview, metadata',
      'Keyboard shortcuts - Work faster',
    ],
  },
  {
    id: 5,
    moduleId: 2,
    moduleName: 'Navigating the Business Glossary',
    title: 'Searching for Terms',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'Basic Search',
          content: [
            'E-Unify\'s powerful search features help you locate any term in seconds.',
            '',
            'How to Search:',
            '1. Click the search bar at the top (or press /)',
            '2. Type any word or phrase',
            '3. Results appear in real-time as you type',
            '',
            'What Search Looks For:',
            '• Term names',
            '• Definitions',
            '• Synonyms',
            '• Acronyms',
            '• Tags',
          ],
        },
        {
          title: 'Search Tips & Tricks',
          content: [
            'Tip 1: Search by Acronym',
            'Type "I-9" to find "Form I-9" and related terms.',
            '',
            'Tip 2: Use Multiple Words',
            'Type "work permit" to find terms containing both words.',
            '',
            'Tip 3: Partial Matching',
            'Type part of a word like "author" to find "authorization," "authorized," "authority," etc.',
            '',
            'Tip 4: Search Synonyms',
            'Type "EAD" to find "Employment Authorization Document" even though EAD isn\'t in the term name.',
          ],
        },
        {
          title: 'Advanced Filters',
          content: [
            'For more precise results, use advanced filters:',
            '',
            'Category Filter: Immigration Forms, Employment Authorization, Verification Processes, Compliance Terms',
            '',
            'Status Filter: Draft, In Review, Approved, Rejected, Archived',
            '',
            'Date Range: Last 7 days, Last 30 days, Last 90 days, Custom range',
            '',
            'Data Steward: Find terms owned by specific people',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What keyboard shortcut focuses the search bar?',
        options: ['Ctrl/Cmd + F', '/ (forward slash)', 'Ctrl/Cmd + S', 'Tab'],
        correctAnswer: 1,
      },
      {
        question: 'What does E-Unify search include?',
        options: ['Only term names', 'Only definitions', 'Term names, definitions, synonyms, and acronyms', 'Only approved terms'],
        correctAnswer: 2,
      },
      {
        question: 'Can you filter search results by date range?',
        options: ['Yes', 'No'],
        correctAnswer: 0,
      },
      {
        question: 'What appears as you type in the search bar?',
        options: ['Nothing until you press Enter', 'Real-time results that update with each letter', 'Only exact matches', 'A loading spinner'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Basic search - Type and see real-time results',
      'Search techniques - Acronyms, partial words, synonyms',
      'Advanced filters - Narrow by category, status, date',
      'Relationship navigation - Follow connections',
      'Keyboard shortcuts - Work faster',
    ],
  },
  {
    id: 6,
    moduleId: 2,
    moduleName: 'Navigating the Business Glossary',
    title: 'Understanding Term Details',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Comprehensive Term Information',
          content: [
            'When you open a term, you see much more than just a definition. E-Unify provides comprehensive information to help you understand and use each term correctly.',
          ],
        },
        {
          title: 'Definition Section',
          content: [
            'Term Name - Large, bold heading',
            'Status Badge - Shows approval status (🟢 Approved, 🔵 In Review, 🟡 Draft, 🔴 Rejected)',
            'Definition - Clear, concise explanation in plain language',
            'Business Context - Explains WHY this term matters',
            'Examples - Real-world usage scenarios',
            'Regulatory References - Links to official sources',
          ],
        },
        {
          title: 'Metadata Section',
          content: [
            'Category: Immigration Forms',
            'Synonyms: I-9, Employment Eligibility Verification Form',
            'Acronyms: I-9',
            'Tags: compliance, verification, employment, USCIS',
            '',
            'Data Steward: Sarah Johnson',
            'Business Owner: HR Department',
            '',
            'Created: January 15, 2024',
            'Last Updated: October 20, 2025',
            'Version: 3.2',
          ],
        },
        {
          title: 'Relationships Section',
          content: [
            'Related Terms:',
            '• E-Verify (used with)',
            '• List A Documents (requires)',
            '• Employment Authorization (verifies)',
            '',
            'Relationship Graph: Click to see visual connections',
            '',
            'Linked Data Assets:',
            '• HR System: Employee table',
            '• Onboarding Portal: I-9 form',
            '• Compliance Dashboard: I-9 report',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What does the Business Context section explain?',
        options: ['The technical definition', 'Why the term matters', 'Who created the term', 'When the term was updated'],
        correctAnswer: 1,
      },
      {
        question: 'Where can you find alternative names for a term?',
        options: ['Definition section', 'Synonyms field in metadata', 'Comments section', 'Activity feed'],
        correctAnswer: 1,
      },
      {
        question: 'What does clicking "View History" show?',
        options: ['Who viewed the term', 'Related terms', 'Previous versions and changes', 'Comments and discussions'],
        correctAnswer: 2,
      },
      {
        question: 'Does the relationship graph show how terms connect visually?',
        options: ['True', 'False'],
        correctAnswer: 0,
      },
    ],
    keyTakeaways: [
      'Definition section - What, why, examples, references',
      'Metadata - Category, tags, ownership, dates',
      'Relationships - Connected terms and assets',
      'Collaboration - Comments and activity tracking',
      'Actions - Follow, share, export, edit',
    ],
  },
  // MODULE 3: Creating and Managing
  {
    id: 7,
    moduleId: 3,
    moduleName: 'Creating and Managing Terms',
    title: 'Creating a New Term',
    duration: '4 minutes',
    content: {
      sections: [
        {
          title: 'Creating a Term - Step by Step',
          content: [
            'Anyone in the organization can propose a new term. The process is designed to be intuitive while ensuring quality.',
            '',
            'Step 1: Click "Create New Term"',
            'Found in the top navigation bar (+ button). Opens a clean, organized form.',
            '',
            'Step 2: Basic Information',
            'Term Name: Use official names (Title Case)',
            'Category: Select from dropdown',
            'Data Steward: Choose responsible person',
          ],
        },
        {
          title: 'Writing the Definition',
          content: [
            'Best Practices for Definitions:',
            '',
            '✓ Clear and Concise (2-3 sentences)',
            'Write as if explaining to a new employee.',
            '',
            '✓ Plain Language',
            'Avoid jargon unless you explain it.',
            '',
            '✓ Complete Information',
            'Include all essential details.',
            '',
            'Example for "Form I-9":',
            '"Form I-9 is used to verify the identity and employment authorization of individuals hired for employment in the United States. All U.S. employers must properly complete Form I-9 for each individual they hire."',
          ],
        },
        {
          title: 'Adding Context and Examples',
          content: [
            'Business Context (2-3 sentences):',
            'Why does this term matter to your organization?',
            '',
            'Example for Form I-9:',
            '"This form is critical for compliance with immigration law. Failure to properly complete and retain I-9 forms can result in significant penalties and legal issues."',
            '',
            'Examples (Minimum 1, Maximum 3):',
            'Real-world scenarios showing usage.',
            '',
            'Example for Form I-9:',
            '"When hiring a new employee, you must complete Section 1 on their first day and Section 2 within 3 business days of their start date."',
          ],
        },
        {
          title: 'Metadata and Relationships',
          content: [
            'Synonyms (Optional):',
            'Alternative names or abbreviations.',
            'Examples: Employment Eligibility Verification Form, I-9',
            '',
            'Acronyms (Optional):',
            'Common abbreviations that appear in the name.',
            'Example: I-9',
            '',
            'Tags (5-10):',
            'Keywords that help with searching and organization.',
            'Examples: compliance, verification, employment, USCIS, HR, hiring',
            '',
            'Regulatory References (Optional but recommended):',
            'Links to official sources and regulations.',
            'Example: https://www.uscis.gov/i-9',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'How many sentences should a definition ideally be?',
        options: ['1', '2-3', '4-5', 'As long as needed'],
        correctAnswer: 1,
      },
      {
        question: 'What format should term names use?',
        options: ['lowercase', 'UPPERCASE', 'Title Case', 'camelCase'],
        correctAnswer: 2,
      },
      {
        question: 'How many tags should each term have?',
        options: ['1-2', '3-4', '5-10', '11-15'],
        correctAnswer: 2,
      },
      {
        question: 'When should you complete Section 2 of Form I-9?',
        options: ['First day', 'Within 3 business days', 'Within 1 week', 'Within 30 days'],
        correctAnswer: 1,
      },
      {
        question: 'What should Business Context explain?',
        options: ['Technical details', 'Why the term matters', 'History of the term', 'Who created the term'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Start with "Create New Term" button',
      'Write clear, plain language definitions (2-3 sentences)',
      'Add business context explaining why it matters',
      'Include real-world examples',
      'Complete metadata with synonyms, acronyms, and tags',
    ],
  },
  {
    id: 8,
    moduleId: 3,
    moduleName: 'Creating and Managing Terms',
    title: 'Editing Existing Terms',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'When to Edit Terms',
          content: [
            'Terms evolve over time. Common reasons to edit:',
            '',
            '• Regulations change',
            '• Procedures update',
            '• Definitions become unclear',
            '• New requirements emerge',
            '• Examples need updating',
            '• References become outdated',
          ],
        },
        {
          title: 'Making Edits',
          content: [
            'Step 1: Open the Term',
            'Navigate to the term you want to edit.',
            '',
            'Step 2: Click Edit',
            'Blue edit button (✏️) appears for terms you can modify.',
            '',
            'Step 3: Make Changes',
            'Edit any field:',
            '- Definition text',
            '- Business context',
            '- Examples',
            '- Metadata',
            '- Tags',
            '- References',
          ],
        },
        {
          title: 'Edit History Tracking',
          content: [
            'E-Unify tracks all changes:',
            '',
            'Version History:',
            '• Version number increments',
            '• Timestamp recorded',
            '• Editor identified',
            '• Changes highlighted',
            '',
            'Audit Trail:',
            '• Who changed what',
            '• When changes were made',
            '• Previous versions viewable',
            '• Change reason documented',
          ],
        },
        {
          title: 'Submit for Review',
          content: [
            'After editing:',
            '',
            'Draft Status:',
            '• Changes saved but not visible to others',
            '• You can continue editing',
            '• No notifications sent',
            '',
            'Submit for Review:',
            '• Data steward notified',
            '• Term enters review process',
            '• Changes visible to reviewers',
            '• Version history preserved',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What button do you click to edit a term?',
        options: ['View', 'Edit', 'Delete', 'Share'],
        correctAnswer: 1,
      },
      {
        question: 'What happens to the original version after editing?',
        options: ['It disappears', 'It\'s saved in version history', 'It\'s deleted', 'Nothing changes'],
        correctAnswer: 1,
      },
      {
        question: 'When you submit edits for review, who is notified?',
        options: ['All users', 'Data steward', 'Nobody', 'Only you'],
        correctAnswer: 1,
      },
      {
        question: 'Can you continue editing after submitting for review?',
        options: ['Yes', 'No'],
        correctAnswer: 0,
      },
      {
        question: 'What information is tracked in edit history?',
        options: ['Only the changes', 'Who, what, and when changed', 'Only who changed', 'Only the date'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Edit terms to keep them accurate and current',
      'All changes are tracked in version history',
      'Submit edits for review when ready',
      'Original versions are never lost',
      'Collaborate through comments on changes',
    ],
  },
  {
    id: 9,
    moduleId: 3,
    moduleName: 'Creating and Managing Terms',
    title: 'Linking Terms and Relationships',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'Types of Relationships',
          content: [
            'E-Unify supports six types of relationships:',
            '',
            '1. RELATED TO (General Connection)',
            '- Use when: Terms are connected but not hierarchical',
            '- Example: "Form I-9" is related to "E-Verify"',
            '',
            '2. IS A TYPE OF (Parent-Child)',
            '- Use when: One term is a category of another',
            '- Example: "Green Card" is a type of "Employment Authorization"',
            '',
            '3. CONTAINS (Composition)',
            '- Use when: One term includes other terms as components',
            '- Example: "Form I-9" contains "Section 1" and "Section 2"',
            '',
            '4. REQUIRES (Dependency)',
            '- Use when: One term depends on another',
            '- Example: "E-Verify" requires "Form I-9"',
            '',
            '5. REPLACES (Succession)',
            '- Use when: One term supersedes another',
            '- Example: "Form I-9 (2023)" replaces "Form I-9 (2020)"',
            '',
            '6. SYNONYM (Same Meaning)',
            '- Use when: Different terms mean the same thing',
            '- Example: "LPR" is synonym of "Lawful Permanent Resident"',
          ],
        },
        {
          title: 'Creating Relationships',
          content: [
            'Step 1: Click "Add Relationship"',
            'Opens a dialog with relationship options.',
            '',
            'Step 2: Select Relationship Type',
            'Choose the most specific type that describes the connection.',
            '',
            'Step 3: Search for the Term',
            'Type the term name. Results appear as you type.',
            '',
            'Step 4: Select the Term',
            'Click on the term to select it.',
            '',
            'Step 5: Describe the Relationship (Optional)',
            'Add context explaining WHY these terms are connected.',
            '',
            'Example: "E-Verify uses information from Form I-9 to verify employment eligibility"',
            '',
            'Step 6: Save the Relationship',
            'Click "Add Relationship" to save.',
            '',
            'Important: Relationships are bidirectional - creating one connection updates both terms automatically.',
          ],
        },
        {
          title: 'Relationship Graph',
          content: [
            'The relationship graph visualizes all connections:',
            '',
            'Features:',
            '- Each node represents a term',
            '- Lines show relationship types',
            '- Solid line = Related To',
            '- Dotted line = Contains',
            '- Arrow = Directional relationship',
            '',
            'Interactions:',
            '- Hover over nodes to see term details',
            '- Click nodes to navigate to that term',
            '- Zoom in/out for detail',
            '- Pan to see more connections',
            '',
            'The graph shows relationships up to 3 levels deep, helping you understand broader context.',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'Which relationship type for parent-child hierarchies?',
        options: ['Related To', 'Is A Type Of', 'Contains', 'Requires'],
        correctAnswer: 1,
      },
      {
        question: 'Are relationships bidirectional?',
        options: ['True', 'False'],
        correctAnswer: 0,
      },
      {
        question: 'What should you add to explain connections?',
        options: ['Nothing', 'A description', 'Multiple relationships', 'Tags'],
        correctAnswer: 1,
      },
      {
        question: 'How many levels does the relationship graph show?',
        options: ['1', '2', '3', 'Unlimited'],
        correctAnswer: 2,
      },
    ],
    keyTakeaways: [
      'Be specific - Use the most precise relationship type',
      'Add context - Describe why terms are related',
      'Avoid redundancy - Don\'t create multiple relationships of same type',
      'Think bidirectionally - Consider how it reads from both directions',
      'Review regularly - Check if relationships are still accurate',
    ],
  },
  {
    id: 10,
    moduleId: 3,
    moduleName: 'Creating and Managing Terms',
    title: 'Collaboration Features',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Comments and Discussions',
          content: [
            'Every term has a comments section for team discussions.',
            '',
            'How to Add a Comment:',
            '1. Scroll to comments section',
            '2. Click "Add Comment"',
            '3. Type your message',
            '4. Use formatting (bold, lists, links)',
            '5. Click "Post Comment"',
            '',
            'Use Comments To:',
            '✅ Ask questions about definitions',
            '✅ Suggest improvements',
            '✅ Share insights or examples',
            '✅ Discuss regulatory changes',
            '✅ Request clarification',
          ],
        },
        {
          title: '@Mentions and Notifications',
          content: [
            'Using @Mentions:',
            '1. Type @ symbol in a comment',
            '2. Select person from dropdown',
            '3. They receive immediate notification',
            '',
            'Example: "@Sarah Johnson - Can you review this definition for accuracy?"',
            '',
            'Notifications You\'ll Receive:',
            '- Someone @mentions you',
            '- Replies to your comments',
            '- Changes to terms you follow',
            '- Review requests',
            '- Approval status updates',
            '',
            'Managing Notifications:',
            '- Click notification bell to see all alerts',
            '- Click any notification to jump to that term/comment',
            '- Mark notifications as read',
          ],
        },
        {
          title: 'Following Terms',
          content: [
            'Why Follow Terms:',
            'Stay informed about specific terms important to your work.',
            '',
            'How to Follow:',
            'Click the "Follow" button on any term page.',
            '',
            'You\'ll Be Notified When:',
            '- Definition is updated',
            '- Someone adds a comment',
            '- Approval status changes',
            '- Relationships are added or removed',
            '',
            'View Followed Terms:',
            'Go to "My Terms" > "Following" to see all terms you\'re following.',
            '',
            'Unfollow:',
            'Click "Following" button to stop receiving notifications.',
          ],
        },
        {
          title: 'Review Requests',
          content: [
            'When to Request Review:',
            'Need formal feedback before submitting for approval.',
            '',
            'How to Request Review:',
            '1. Click "Request Review" button',
            '2. Select reviewers (data stewards, experts, colleagues)',
            '3. Add message explaining what you need',
            '4. Click "Send Request"',
            '',
            'Example Message:',
            '"Please review this definition for technical accuracy before I submit for approval."',
            '',
            'Reviewers Receive:',
            '- Notification of review request',
            '- Link to the term',
            '- Your message',
            '- Ability to provide feedback via comments',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What happens when you @mention someone?',
        options: ['Nothing', 'They receive an immediate notification', 'Email is sent', 'Term is assigned to them'],
        correctAnswer: 1,
      },
      {
        question: 'When should you follow a term?',
        options: ['Never', 'Only terms you created', 'Terms you want to stay informed about', 'All terms'],
        correctAnswer: 2,
      },
      {
        question: 'Can comments include formatting?',
        options: ['True', 'False'],
        correctAnswer: 0,
      },
      {
        question: 'What is "Request Review" for?',
        options: ['To approve a term', 'To get formal feedback from experts', 'To delete a term', 'To archive a term'],
        correctAnswer: 1,
      },
      {
        question: 'Which trigger notifications? (Select all)',
        options: ['Someone @mentions you', 'A term you follow is updated', 'Someone views a term', 'Someone replies to your comment'],
        correctAnswer: 0,
      },
    ],
    keyTakeaways: [
      'Use comments for questions and discussions',
      '@mention specific people for their input',
      'Follow terms to stay informed',
      'Request formal reviews when needed',
      'All collaboration is tracked and notified',
    ],
  },
  // MODULE 4: Governance and Best Practices
  {
    id: 11,
    moduleId: 4,
    moduleName: 'Governance and Best Practices',
    title: 'Approval Workflow',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'Workflow Overview',
          content: [
            'Every term goes through a structured approval process:',
            '',
            '1. CREATE - Anyone can propose a new term',
            '2. DRAFT - Term is saved, visible only to creator',
            '3. SUBMIT - Creator submits for review',
            '4. REVIEW - Data steward evaluates quality',
            '5. APPROVE or REJECT - Data steward makes decision',
            '6. PUBLISH - Approved terms become official',
            '',
            'Status Indicators:',
            '🟡 Draft - Work in progress',
            '🔵 In Review - Awaiting data steward',
            '🟢 Approved - Official definition',
            '🔴 Rejected - Needs revision',
            '⚫ Archived - No longer active',
          ],
        },
        {
          title: 'Roles and Responsibilities',
          content: [
            'CONTRIBUTORS:',
            '- Create and edit draft terms',
            '- Submit for review',
            '- Respond to feedback',
            '- Update based on comments',
            '',
            'DATA STEWARDS:',
            '- Review submitted terms',
            '- Approve or reject definitions',
            '- Provide constructive feedback',
            '- Ensure quality standards',
            '- Maintain term accuracy',
            '',
            'ADMINISTRATORS:',
            '- Manage user permissions',
            '- Configure workflows',
            '- Monitor system health',
            '- Generate reports',
          ],
        },
        {
          title: 'Approval Criteria',
          content: [
            'Data stewards evaluate terms based on:',
            '',
            '✓ ACCURACY - Information is correct and verified',
            '✓ CLARITY - Definition is easy to understand',
            '✓ COMPLETENESS - All required fields filled',
            '✓ CONSISTENCY - Matches style guidelines',
            '✓ RELEVANCE - Appropriate for glossary',
            '✓ COMPLIANCE - Meets regulatory requirements',
            '',
            'If a term doesn\'t meet standards:',
            '- Reject with specific feedback',
            '- Request particular changes',
            '- Suggest improvements',
            '- Provide examples',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What status indicates awaiting review?',
        options: ['Draft', 'In Review', 'Approved', 'Rejected'],
        correctAnswer: 1,
      },
      {
        question: 'Who can approve term definitions?',
        options: ['All users', 'Contributors', 'Data Stewards', 'Anyone'],
        correctAnswer: 2,
      },
      {
        question: 'What should data stewards do if term doesn\'t meet standards?',
        options: ['Approve it anyway', 'Delete it', 'Reject with specific feedback', 'Ignore it'],
        correctAnswer: 2,
      },
      {
        question: 'How long should data stewards take to review?',
        options: ['1 hour', '48 hours', '1 week', '1 month'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Workflow ensures quality through structured process',
      'Clear roles and responsibilities for all participants',
      'Data stewards are gatekeepers of quality',
      'Constructive feedback helps improve definitions',
      'All actions are tracked for audit purposes',
    ],
  },
  {
    id: 12,
    moduleId: 4,
    moduleName: 'Governance and Best Practices',
    title: 'Data Stewardship',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'Steward Responsibilities',
          content: [
            'Data stewards are responsible for:',
            '',
            'QUALITY ASSURANCE:',
            '- Review all term submissions',
            '- Verify accuracy of definitions',
            '- Ensure clarity and completeness',
            '- Check regulatory compliance',
            '',
            'MAINTENANCE:',
            '- Update terms when regulations change',
            '- Review terms quarterly',
            '- Archive outdated terms',
            '- Resolve conflicts and ambiguities',
            '',
            'COLLABORATION:',
            '- Train team members',
            '- Answer questions',
            '- Provide guidance',
            '- Share best practices',
            '',
            'GOVERNANCE:',
            '- Enforce quality standards',
            '- Monitor adoption metrics',
            '- Report on glossary health',
            '- Recommend improvements',
          ],
        },
        {
          title: 'Daily Activities',
          content: [
            'Typical time commitment: 3-4 hours per week for 100-200 terms',
            '',
            'Morning (50 min):',
            '- Check pending reviews (15 min)',
            '- Approve quality submissions (20 min)',
            '- Provide feedback on drafts (15 min)',
            '',
            'Midday (65 min):',
            '- Answer user questions (20 min)',
            '- Update terms for regulatory changes (30 min)',
            '- Review relationship accuracy (15 min)',
            '',
            'Afternoon (75 min):',
            '- Conduct quality audits (30 min)',
            '- Train new contributors (30 min)',
            '- Generate reports (15 min)',
          ],
        },
        {
          title: 'Success Metrics',
          content: [
            'QUALITY METRICS:',
            '- Approval rate: Target 80%+',
            '- Rejection rate: Target <20%',
            '- Average review time: Target <48 hours',
            '- Revision rate: Target <30%',
            '',
            'ADOPTION METRICS:',
            '- Term usage: Track views and searches',
            '- Coverage: % of data assets linked',
            '- User satisfaction: Survey scores',
            '- Training completion: % of users trained',
            '',
            'COMPLIANCE METRICS:',
            '- Regulatory alignment: 100%',
            '- Audit findings: Target 0',
            '- Update timeliness: Within 30 days',
            '- Documentation completeness: 95%+',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'How often should data stewards review terms?',
        options: ['Daily', 'Weekly', 'Quarterly', 'Annually'],
        correctAnswer: 2,
      },
      {
        question: 'What is the target approval rate?',
        options: ['50%', '60%', '70%', '80%+'],
        correctAnswer: 3,
      },
      {
        question: 'Time per week for stewardship?',
        options: ['1 hour', '3-4 hours', '10 hours', '20 hours'],
        correctAnswer: 1,
      },
      {
        question: 'What should stewards do when regulations change?',
        options: ['Wait for users to report', 'Update affected terms proactively', 'Archive all related terms', 'Nothing'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Be responsive - Review within 48 hours',
      'Be constructive - Provide helpful feedback',
      'Be consistent - Apply standards fairly',
      'Be proactive - Update before issues arise',
      'Be collaborative - Work with contributors',
    ],
  },
  {
    id: 13,
    moduleId: 4,
    moduleName: 'Governance and Best Practices',
    title: 'Quality Standards',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Quality Criteria',
          content: [
            'High-quality terms meet these standards:',
            '',
            'DEFINITION QUALITY:',
            '✅ Clear and concise (2-3 sentences)',
            '✅ Plain language (no unexplained jargon)',
            '✅ Accurate and verified',
            '✅ Complete (includes essential information)',
            '✅ Grammatically correct',
            '',
            'BUSINESS CONTEXT:',
            '✅ Explains why it matters',
            '✅ Describes impact',
            '✅ Relevant to organization',
            '✅ 2-3 sentences',
            '',
            'EXAMPLES:',
            '✅ At least one provided',
            '✅ Real-world scenarios',
            '✅ Easy to understand',
            '✅ Demonstrates usage',
            '',
            'METADATA:',
            '✅ Category assigned',
            '✅ Synonyms listed',
            '✅ Acronyms provided',
            '✅ Tags added (5-10)',
            '✅ Data steward assigned',
            '✅ Regulatory references cited',
          ],
        },
        {
          title: 'Naming Conventions',
          content: [
            'TERM NAMES:',
            '✅ Use title case: "Employment Authorization"',
            '✅ Be specific: "Form I-9" not "Form"',
            '✅ Avoid abbreviations (use acronyms field)',
            '✅ Keep concise: 2-5 words ideal',
            '✅ Use official names matching regulations',
            '',
            'AVOID:',
            '❌ All caps: "FORM I-9"',
            '❌ Vague names: "Document"',
            '❌ Internal jargon: "The EV thing"',
            '❌ Abbreviations in name: "EAD"',
            '❌ Redundancy: "Form I-9 Form"',
          ],
        },
        {
          title: 'Consistency Guidelines',
          content: [
            'STYLE:',
            '- Same voice and tone',
            '- Follow grammar rules',
            '- Apply formatting consistently',
            '- Use standard punctuation',
            '',
            'STRUCTURE:',
            '- Same field order',
            '- Similar definition length',
            '- Consistent example format',
            '- Standard reference citation',
            '',
            'TERMINOLOGY:',
            '- Use glossary terms in definitions',
            '- Link related concepts',
            '- Avoid contradictions',
            '- Maintain accuracy',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'How long should a definition be?',
        options: ['1 sentence', '2-3 sentences', '1 paragraph', 'As long as needed'],
        correctAnswer: 1,
      },
      {
        question: 'What case should term names use?',
        options: ['lowercase', 'UPPERCASE', 'Title Case', 'camelCase'],
        correctAnswer: 2,
      },
      {
        question: 'How many tags should each term have?',
        options: ['1-2', '3-4', '5-10', '20+'],
        correctAnswer: 2,
      },
    ],
    keyTakeaways: [
      'Quality definitions are clear, accurate, and complete',
      'Follow naming conventions for consistency',
      'Maintain consistent style across all terms',
      'Provide context and examples for understanding',
      'Complete metadata for better findability',
    ],
  },
  {
    id: 14,
    moduleId: 4,
    moduleName: 'Governance and Best Practices',
    title: 'Maintenance and Updates',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Review Schedule',
          content: [
            'Establish a maintenance schedule:',
            '',
            'DAILY:',
            '- Monitor new submissions',
            '- Answer user questions',
            '- Address urgent updates',
            '',
            'WEEKLY:',
            '- Review pending terms',
            '- Check for regulatory updates',
            '- Process feedback',
            '',
            'MONTHLY:',
            '- Audit term quality',
            '- Update statistics',
            '- Review adoption metrics',
            '',
            'QUARTERLY:',
            '- Review all terms for accuracy',
            '- Update outdated information',
            '- Archive obsolete terms',
            '- Assess coverage gaps',
            '',
            'ANNUALLY:',
            '- Comprehensive glossary audit',
            '- Update governance policies',
            '- Review user satisfaction',
            '- Plan improvements',
          ],
        },
        {
          title: 'Handling Changes',
          content: [
            'When regulations or processes change:',
            '',
            'STEP 1: IDENTIFY IMPACT',
            '- Which terms are affected?',
            '- What needs updating?',
            '- Who needs to know?',
            '',
            'STEP 2: UPDATE TERMS',
            '- Revise definitions',
            '- Update references',
            '- Add effective dates',
            '- Document changes',
            '',
            'STEP 3: COMMUNICATE',
            '- Notify affected users',
            '- Update training materials',
            '- Send announcements',
            '- Schedule reviews',
            '',
            'STEP 4: VERIFY',
            '- Confirm accuracy',
            '- Check related terms',
            '- Update linked assets',
            '- Test workflows',
          ],
        },
        {
          title: 'Deprecation Process',
          content: [
            'For outdated terms:',
            '',
            '1. Mark as "Deprecated"',
            '2. Add deprecation notice',
            '3. Link to replacement term',
            '4. Set archive date',
            '5. Notify users',
            '6. Archive after transition period',
            '',
            'Important: Never delete terms - archive them for historical reference.',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'How often should all terms be reviewed?',
        options: ['Weekly', 'Monthly', 'Quarterly', 'Annually'],
        correctAnswer: 2,
      },
      {
        question: 'What should you do with outdated terms?',
        options: ['Delete them', 'Archive them', 'Leave them as-is', 'Hide them'],
        correctAnswer: 1,
      },
      {
        question: 'When regulations change, what\'s the first step?',
        options: ['Update all terms immediately', 'Identify which terms are affected', 'Archive old terms', 'Create new terms'],
        correctAnswer: 1,
      },
      {
        question: 'What is the target review time?',
        options: ['24 hours', '48 hours', '1 week', '1 month'],
        correctAnswer: 1,
      },
      {
        question: 'What should be in every term?',
        options: ['Definition only', 'Definition and examples', 'Definition, context, examples, and metadata', 'Just the term name'],
        correctAnswer: 2,
      },
    ],
    keyTakeaways: [
      'Regular reviews keep terms accurate and relevant',
      'Systematic approach to handling regulatory changes',
      'Archive rather than delete to maintain history',
      'Communicate changes to all stakeholders',
      'Monitor metrics to measure success',
    ],
  },
  // MODULE 5: Advanced Features
  {
    id: 15,
    moduleId: 5,
    moduleName: 'Advanced Features',
    title: 'Categories and Taxonomies',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Organizing Terms by Category',
          content: [
            'Categories help you organize and classify terms logically.',
            '',
            'E-Unify\'s Default Categories:',
            '📋 Immigration Forms - I-9, I-94, I-797, etc.',
            '🔐 Employment Authorization - Work permits, visas, green cards',
            '✅ Verification Processes - E-Verify, document review',
            '📊 Compliance Terms - Regulations, audit procedures',
          ],
        },
        {
          title: 'Creating Hierarchies',
          content: [
            'Build parent-child relationships using "Is A Type Of":',
            '',
            'Example Hierarchy:',
            'Employment Authorization (parent)',
            '├── Green Card (child)',
            '├── Work Permit (child)',
            '│   ├── H-1B Visa (grandchild)',
            '│   └── L-1 Visa (grandchild)',
            '└── Employment Authorization Document (child)',
            '',
            'Benefits:',
            '- Clear organization',
            '- Easy navigation',
            '- Better understanding of relationships',
            '- Improved searchability',
          ],
        },
        {
          title: 'Custom Taxonomies',
          content: [
            'Create custom categories for your organization\'s needs:',
            '',
            'Steps:',
            '1. Identify common themes in your terms',
            '2. Group related concepts',
            '3. Create category structure',
            '4. Assign terms to categories',
            '5. Review and refine',
            '',
            'Best Practices:',
            '✓ Keep categories broad enough to be useful',
            '✓ Avoid too many levels (3 max recommended)',
            '✓ Use consistent naming',
            '✓ Review taxonomy quarterly',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What is a taxonomy?',
        options: ['A type of term', 'A hierarchical classification system', 'A search feature', 'A user role'],
        correctAnswer: 1,
      },
      {
        question: 'How many top-level categories recommended?',
        options: ['2-3', '4-8', '10-15', '20+'],
        correctAnswer: 1,
      },
      {
        question: 'Can terms belong to multiple categories?',
        options: ['Yes', 'No'],
        correctAnswer: 0,
      },
    ],
    keyTakeaways: [
      'Categories organize terms logically',
      'Hierarchies show parent-child relationships',
      'Custom taxonomies meet specific needs',
      'Keep structure simple and consistent',
      'Review and refine categories regularly',
    ],
  },
  {
    id: 16,
    moduleId: 5,
    moduleName: 'Advanced Features',
    title: 'Reporting and Analytics',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Usage Statistics Dashboard',
          content: [
            'Track how your Business Glossary is being used:',
            '',
            'Key Metrics:',
            '- Total Terms: Number of definitions in glossary',
            '- Approved Terms: Official definitions ready for use',
            '- Pending Review: Terms awaiting approval',
            '- Active Users: People using the glossary',
            '',
            'Most Viewed Terms:',
            'See which definitions are most popular. This helps identify:',
            '- Critical business concepts',
            '- Areas needing more clarity',
            '- Training opportunities',
            '',
            'Search Analytics:',
            '- Most common searches',
            '- Searches with no results (gaps in glossary)',
            '- Search trends over time',
          ],
        },
        {
          title: 'Term Adoption Metrics',
          content: [
            'Coverage Percentage:',
            '% of data assets linked to glossary terms',
            '',
            'Example:',
            '- Total data assets: 500',
            '- Linked to terms: 400',
            '- Coverage: 80%',
            '',
            'User Engagement:',
            '- Number of searches per day',
            '- Terms created per month',
            '- Comments and discussions',
            '- Review requests submitted',
            '',
            'Quality Scores:',
            '- % of terms with complete metadata',
            '- % of terms with examples',
            '- % of terms with regulatory references',
            '- Average time to approval',
          ],
        },
        {
          title: 'Gap Analysis',
          content: [
            'Identify areas needing attention:',
            '',
            'Missing Terms:',
            '- Searches with no results',
            '- Undefined acronyms',
            '- Unlinked data assets',
            '',
            'Outdated Terms:',
            '- Not updated in 12+ months',
            '- Broken regulatory links',
            '- Deprecated but not archived',
            '',
            'Low-Quality Terms:',
            '- Missing business context',
            '- No examples',
            '- Incomplete metadata',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'What does coverage percentage measure?',
        options: ['Number of users', '% of data assets linked to terms', 'Number of searches', 'Approval rate'],
        correctAnswer: 1,
      },
      {
        question: 'How often review analytics?',
        options: ['Daily', 'Weekly', 'Monthly', 'Annually'],
        correctAnswer: 2,
      },
      {
        question: 'What indicates low adoption?',
        options: ['High view counts', 'Many searches', 'Low view counts and searches', 'Many comments'],
        correctAnswer: 2,
      },
    ],
    keyTakeaways: [
      'Dashboards show usage trends and metrics',
      'Coverage indicates term adoption',
      'Analytics identify gaps and opportunities',
      'Regular monitoring improves glossary health',
      'Data-driven decisions enhance quality',
    ],
  },
  {
    id: 17,
    moduleId: 5,
    moduleName: 'Advanced Features',
    title: 'Integration with Data Assets',
    duration: '1 minute',
    content: {
      sections: [
        {
          title: 'Linking Terms to Databases',
          content: [
            'Connect glossary terms to actual data:',
            '',
            'Benefits:',
            '- Traceability - See where terms are used',
            '- Impact Analysis - Understand change effects',
            '- Automated Governance - Enforce standards',
            '- Better Compliance - Track data lineage',
            '',
            'How to Link:',
            '1. Open term detail page',
            '2. Click "Link Data Asset"',
            '3. Search for database/table/column',
            '4. Select asset',
            '5. Save link',
          ],
        },
        {
          title: 'Connecting to Reports',
          content: [
            'Link terms to reports and dashboards:',
            '',
            'Example:',
            '- Term: "Form I-9"',
            '- Linked Reports:',
            '  - I-9 Compliance Dashboard',
            '  - Audit Readiness Report',
            '  - HR Metrics Summary',
            '',
            'Users can:',
            '- See which reports use this term',
            '- Navigate from term to report',
            '- Understand data context',
          ],
        },
        {
          title: 'API Integration',
          content: [
            'Access glossary programmatically:',
            '',
            'Use Cases:',
            '- Automated tagging of data',
            '- Integration with BI tools',
            '- Custom applications',
            '- Data catalogs',
            '',
            'API Endpoints:',
            '- GET /api/terms - List all terms',
            '- GET /api/terms/:id - Get term details',
            '- POST /api/terms - Create new term',
            '- PUT /api/terms/:id - Update term',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'Why link terms to data assets?',
        options: ['It looks nice', 'Traceability and governance', 'Required by law', 'No reason'],
        correctAnswer: 1,
      },
      {
        question: 'How access API?',
        options: ['It\'s not available', 'Through developer documentation', 'Only administrators', 'Email support'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Linking provides traceability and governance',
      'Reports show term usage in context',
      'API enables programmatic access',
      'Integration enhances data understanding',
      'Automation improves consistency',
    ],
  },
  // MODULE 6: Practical Application
  {
    id: 18,
    moduleId: 6,
    moduleName: 'Practical Application',
    title: 'Scenario - New Regulation',
    duration: '3 minutes',
    content: {
      sections: [
        {
          title: 'The Scenario',
          content: [
            'USCIS updates Form I-9 requirements effective January 1, 2026',
            '',
            'New requirements:',
            '- Retention period changes from 3 to 5 years',
            '- New acceptable documents added to List A',
            '- Remote verification procedures updated',
            '',
            'Your Task: Update the Business Glossary to reflect these changes.',
          ],
        },
        {
          title: 'Step-by-Step Solution',
          content: [
            'Step 1: Identify Affected Terms (15 min)',
            '',
            'Search for related terms:',
            '- Form I-9 ✓',
            '- List A Documents ✓',
            '- Retention Requirements ✓',
            '- Remote Verification ✓',
            '- E-Verify (may be affected)',
            '',
            'Create a checklist of terms to update.',
            '',
            'Step 2: Update Form I-9 Definition (10 min)',
            '',
            'Old definition:',
            '> "Employers must retain Form I-9 for three years after hire date or one year after termination, whichever is later."',
            '',
            'New definition:',
            '> "Employers must retain Form I-9 for five years after hire date or one year after termination, whichever is later."',
            '',
            'Add change note:',
            '> "Updated retention period per USCIS regulation change effective January 1, 2026. Reference: 8 CFR § 274a.2 (2026)."',
            '',
            'Step 3: Update Related Terms (20 min)',
            '',
            'List A Documents:',
            '- Add new acceptable documents',
            '- Update examples',
            '- Add effective date',
            '',
            'Remote Verification:',
            '- Update procedures',
            '- Add new requirements',
            '- Link to USCIS guidance',
            '',
            'Step 4: Update References (10 min)',
            '',
            '- Update all regulatory links',
            '- Add new USCIS guidance documents',
            '- Update version numbers',
            '- Add effective dates',
            '',
            'Step 5: Communicate Changes (15 min)',
            '',
            '- Post comment on each updated term',
            '- @mention relevant stakeholders',
            '- Send email announcement',
            '- Update training materials',
            '',
            'Step 6: Verify and Test (10 min)',
            '',
            '- Review all changes for accuracy',
            '- Check related terms for consistency',
            '- Verify links work',
            '- Test search functionality',
          ],
        },
        {
          title: 'Results',
          content: [
            'Timeline: 80 minutes total',
            'Terms Updated: 4',
            'Stakeholders Notified: 50+ users',
            'Compliance: Achieved before effective date',
            '',
            'Without Business Glossary:',
            '- 6 weeks to update all documentation',
            '- Inconsistent implementation',
            '- Risk of non-compliance',
            '',
            'With Business Glossary:',
            '- 80 minutes to update',
            '- Instant notification to all users',
            '- Consistent, compliant implementation',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'How many terms did we identify for updating?',
        options: ['3', '4', '5', '6'],
        correctAnswer: 1,
      },
      {
        question: 'What is the new retention period for Form I-9?',
        options: ['3 years', '4 years', '5 years', '6 years'],
        correctAnswer: 2,
      },
      {
        question: 'How long did the entire update take?',
        options: ['30 minutes', '60 minutes', '80 minutes', '2 hours'],
        correctAnswer: 2,
      },
      {
        question: 'What is the retention period compared to?',
        options: ['Only 3 years', 'Only 1 year after termination', '3 years after hire OR 1 year after termination, whichever is later', '5 years no matter what'],
        correctAnswer: 2,
      },
    ],
    keyTakeaways: [
      'Centralized updates save time',
      'Automatic notifications ensure awareness',
      'Version control provides audit trail',
      'Linked terms maintain consistency',
      '80 minutes vs 6 weeks shows dramatic improvement',
    ],
  },
  {
    id: 19,
    moduleId: 6,
    moduleName: 'Practical Application',
    title: 'Hands-On Exercise & Course Completion',
    duration: '2 minutes',
    content: {
      sections: [
        {
          title: 'Final Hands-On Exercise',
          content: [
            'Your Task: Create a complete term from scratch',
            '',
            'Term to Create: "Temporary Protected Status (TPS)"',
            '',
            'Requirements:',
            '1. Term Name: Use proper title case',
            '2. Definition: 2-3 sentences, plain language',
            '3. Business Context: Why it matters for employment',
            '4. Example: Real-world scenario',
            '5. Metadata: Category, synonyms, tags',
            '6. Regulatory Reference: Cite official source',
            '7. Relationships: Link to at least 2 related terms',
            '',
            'Time Limit: 15 minutes',
          ],
        },
        {
          title: 'Self-Assessment Checklist',
          content: [
            'Before submitting, verify:',
            '',
            '✅ Term name is properly formatted',
            '✅ Definition is clear and concise',
            '✅ Business context explains importance',
            '✅ Example shows real usage',
            '✅ All metadata fields complete',
            '✅ Regulatory references cited',
            '✅ Relationships added',
            '✅ No spelling/grammar errors',
          ],
        },
        {
          title: 'Course Summary',
          content: [
            'Congratulations! You\'ve completed the E-Unify Business Glossary Training!',
            '',
            'What You\'ve Learned:',
            '',
            'Module 1: Introduction',
            '- ✓ What a Business Glossary is',
            '- ✓ E-Unify\'s features',
            '- ✓ Real-world benefits',
            '',
            'Module 2: Navigation',
            '- ✓ Interface overview',
            '- ✓ Search techniques',
            '- ✓ Term details',
            '',
            'Module 3: Creating & Managing',
            '- ✓ Creating new terms',
            '- ✓ Editing existing terms',
            '- ✓ Building relationships',
            '- ✓ Collaboration features',
            '',
            'Module 4: Governance',
            '- ✓ Approval workflows',
            '- ✓ Data stewardship',
            '- ✓ Quality standards',
            '- ✓ Maintenance procedures',
            '',
            'Module 5: Advanced Features',
            '- ✓ Categories and taxonomies',
            '- ✓ Reporting and analytics',
            '- ✓ Data asset integration',
            '',
            'Module 6: Practical Application',
            '- ✓ Real-world scenarios',
            '- ✓ Hands-on practice',
          ],
        },
        {
          title: 'Final Assessment',
          content: [
            '20 Questions covering all modules',
            '',
            'Passing Score: 80% (16/20 correct)',
            '',
            'Topics Covered:',
            '- Business Glossary fundamentals (5 questions)',
            '- Creating and managing terms (5 questions)',
            '- Governance and best practices (5 questions)',
            '- Advanced features (5 questions)',
            '',
            'Time Limit: 30 minutes',
          ],
        },
        {
          title: 'Certificate of Completion',
          content: [
            'Upon passing the final assessment, you\'ll receive:',
            '',
            'E-Unify Business Glossary Certification',
            '- Valid for 12 months',
            '- Digital badge for LinkedIn',
            '- Proof of training completion',
            '- Access to advanced courses',
          ],
        },
        {
          title: 'Next Steps',
          content: [
            'Continue Your Learning:',
            '1. Practice - Create 5 terms in E-Unify',
            '2. Collaborate - Help colleagues with their terms',
            '3. Contribute - Join as a data steward',
            '4. Advance - Take advanced governance course',
            '',
            'Resources:',
            '- Quick reference guides (downloadable PDFs)',
            '- Video tutorials',
            '- Community forum',
            '- Support documentation',
          ],
        },
        {
          title: 'Thank You!',
          content: [
            'Thank you for completing the E-Unify Business Glossary Training!',
            '',
            'You\'re now equipped to:',
            '✅ Create high-quality term definitions',
            '✅ Navigate E-Unify efficiently',
            '✅ Collaborate with your team',
            '✅ Follow governance best practices',
            '✅ Contribute to data governance excellence',
            '',
            'Questions? Contact your data steward or E-Unify support.',
          ],
        },
      ],
    },
    quiz: [
      {
        question: 'How many questions are on the final assessment?',
        options: ['10', '15', '20', '25'],
        correctAnswer: 2,
      },
      {
        question: 'What score do you need to pass the final assessment?',
        options: ['70%', '75%', '80%', '85%'],
        correctAnswer: 2,
      },
      {
        question: 'How long is your certification valid?',
        options: ['6 months', '12 months', '2 years', 'Lifetime'],
        correctAnswer: 1,
      },
      {
        question: 'What should you do to practice what you learned?',
        options: ['Read more documentation', 'Create 5 terms in E-Unify', 'Email support', 'Wait for instructions'],
        correctAnswer: 1,
      },
    ],
    keyTakeaways: [
      'Complete all modules with hands-on practice',
      'Pass final assessment to receive certification',
      'Apply knowledge by creating terms',
      'Continue learning with advanced courses',
      'Contribute to your organization\'s data governance',
    ],
  },
];

const CERT_DIALOG_TITLE_ID = 'business-glossary-certificate-dialog-title';
const CERT_DIALOG_DESCRIPTION_ID = 'business-glossary-certificate-dialog-description';
const CERTIFICATE_CONTENT_ID = 'certificate-content';

const formatCertificateDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const createCertificateSerial = (issuedAt: Date): string => {
  const randomSegment = issuedAt.getTime().toString(36).toUpperCase().slice(-6);
  const dateSegment = `${issuedAt.getFullYear()}${(issuedAt.getMonth() + 1)
    .toString()
    .padStart(2, '0')}${issuedAt.getDate().toString().padStart(2, '0')}`;
  return `EUG-${dateSegment}-${randomSegment}`;
};

const BusinessGlossaryTraining: React.FC = () => {
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [completedLessons, setCompletedLessons] = useState<number[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<{ [key: number]: number }>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [certificateIssuedAt, setCertificateIssuedAt] = useState<Date | null>(null);
  const [certificateSerialNumber, setCertificateSerialNumber] = useState('');

  const certificateHighlights = useMemo(
    () => [
      `Completed all ${lessons.length} lessons and scenario exercises`,
      'Mastered glossary governance, terminology alignment, and stewardship collaboration',
      'Certification valid for 12 months with eligibility for advanced programs',
    ],
    []
  );

  const certificateIssueDateLabel = useMemo(
    () => (certificateIssuedAt ? formatCertificateDate(certificateIssuedAt) : ''),
    [certificateIssuedAt]
  );

  const certificateExpiryDateLabel = useMemo(() => {
    if (!certificateIssuedAt) {
      return '';
    }
    const expiry = new Date(certificateIssuedAt);
    expiry.setFullYear(expiry.getFullYear() + 1);
    return formatCertificateDate(expiry);
  }, [certificateIssuedAt]);

  const displayedIssueDate = useMemo(() => {
    if (certificateIssueDateLabel) {
      return certificateIssueDateLabel;
    }
    return formatCertificateDate(new Date());
  }, [certificateIssueDateLabel]);

  const displayedExpiryDate = useMemo(() => {
    if (certificateExpiryDateLabel) {
      return certificateExpiryDateLabel;
    }
    const fallback = new Date();
    fallback.setFullYear(fallback.getFullYear() + 1);
    return formatCertificateDate(fallback);
  }, [certificateExpiryDateLabel]);

  const displayedSerialNumber = certificateSerialNumber || 'EUG-PENDING';

  const handleOpenCertificateDialog = useCallback(() => {
    const issuedAt = new Date();
    setCertificateIssuedAt(issuedAt);
    setCertificateSerialNumber(createCertificateSerial(issuedAt));
    setShowCertificateDialog(true);
  }, []);

  const handleCloseCertificateDialog = useCallback(() => {
    setShowCertificateDialog(false);
  }, []);

  const handleCertificatePrint = useCallback(() => {
    if (!studentName) {
      window.alert('Please enter your full name before downloading the certificate.');
      return;
    }

    const certificateElement = document.getElementById(CERTIFICATE_CONTENT_ID);
    if (!certificateElement) {
      console.error('Certificate content not found.');
      return;
    }

    const issuedDate = certificateIssuedAt ?? new Date();
    const formattedDate = issuedDate.toISOString().split('T')[0];
    const rawName = studentName.trim();
    const sanitizedName = rawName
      ? rawName.replace(/[<>:"/\\|?*]+/g, '').replace(/\s+/g, ' ')
      : 'Student';
    const filename = `E-Unify Business Glossary Certificate - ${sanitizedName} - ${formattedDate}`;

    const certificateHtml = certificateElement.outerHTML;
    const collectedStyles = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]')
    )
      .map((node) => node.outerHTML)
      .join('');

    const printWindow = window.open('', '_blank', 'width=1200,height=850');
    if (!printWindow) {
      window.alert('Please enable pop-ups to print or save your certificate.');
      return;
    }

    const printStyles = `
      <style>
        @page {
          size: letter landscape;
          margin: 0.5in;
        }

        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          background: #f4f7fb;
        }

        body {
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Source Sans Pro', Arial, sans-serif;
        }

        #${CERTIFICATE_CONTENT_ID} {
          margin: 0 auto;
          width: 100%;
          max-width: 10.5in;
          box-sizing: border-box;
          page-break-inside: avoid;
          display: block !important;
        }

        #${CERTIFICATE_CONTENT_ID} * {
          box-sizing: border-box;
        }

        /* Fix grid layout for print - REMOVED since we no longer use grid */
        /* #${CERTIFICATE_CONTENT_ID} .MuiGrid-container {
          display: table !important;
          width: 100% !important;
          border-collapse: separate !important;
          border-spacing: 0.5in 0 !important;
          margin-bottom: 1.5rem !important;
        }

        #${CERTIFICATE_CONTENT_ID} .MuiGrid-item {
          display: table-cell !important;
          width: 45% !important;
          vertical-align: top !important;
          padding: 0 !important;
        }

        #${CERTIFICATE_CONTENT_ID} .MuiGrid-item:first-child {
          padding-right: 0.25in !important;
        }

        #${CERTIFICATE_CONTENT_ID} .MuiGrid-item:last-child {
          padding-left: 0.25in !important;
        } */

        /* Ensure proper spacing */
        #${CERTIFICATE_CONTENT_ID} > div {
          margin-bottom: 1.5rem;
        }

        /* Fix list styling for print - REMOVED since we no longer use lists */
        /* #${CERTIFICATE_CONTENT_ID} .MuiList-root {
          padding-left: 0 !important;
        }

        #${CERTIFICATE_CONTENT_ID} .MuiListItem-root {
          padding: 0.125rem 0 !important;
        } */

        /* Ensure images print properly */
        #${CERTIFICATE_CONTENT_ID} img {
          max-width: 100% !important;
          height: auto !important;
        }

        /* Fix absolute positioning for print */
        #${CERTIFICATE_CONTENT_ID} [style*="position: absolute"] {
          position: absolute !important;
        }

        /* Ensure certificate sections don't overlap */
        #${CERTIFICATE_CONTENT_ID} > div {
          position: relative !important;
          clear: both !important;
        }

        /* Fix decorative borders for print */
        #${CERTIFICATE_CONTENT_ID} [aria-hidden="true"] {
          position: absolute !important;
          z-index: -1 !important;
        }

        /* Ensure text content is above decorative elements */
        #${CERTIFICATE_CONTENT_ID} [role="document"] {
          position: relative !important;
          z-index: 1 !important;
        }

        /* Force proper stacking context */
        #${CERTIFICATE_CONTENT_ID} {
          isolation: isolate;
        }
      </style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Source+Sans+Pro:wght@400;600;700&display=swap" rel="stylesheet" />
    `;

    printWindow.document.open();
    printWindow.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${filename}</title>${collectedStyles}${printStyles}</head><body>${certificateHtml}</body></html>`
    );
    printWindow.document.close();

    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, [certificateIssuedAt, studentName]);

  const certificateNameFieldId = 'certificate-full-name';
  const certificateNameHelperId = 'certificate-name-helper';

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isJumpShortcut =
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        (e.key === 'End' || e.key === 'ArrowDown');

      if (isJumpShortcut) {
        e.preventDefault();
        const lastLessonIndex = lessons.length - 1;
        const priorLessonIds = lessons.slice(0, lastLessonIndex).map(lesson => lesson.id);
        setCompletedLessons(prev => Array.from(new Set([...prev, ...priorLessonIds])));
        setCurrentLessonIndex(lastLessonIndex);
        setQuizAnswers({});
        setShowQuizResults(false);
        setCourseCompleted(false);
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const currentLesson = lessons[currentLessonIndex];
  const progress = ((currentLessonIndex + 1) / lessons.length) * 100;
  const allLessonsCompleted = completedLessons.length === lessons.length;

  const handleNextLesson = () => {
    if (currentLessonIndex < lessons.length - 1) {
      // Mark current lesson as completed
      if (!completedLessons.includes(currentLesson.id)) {
        setCompletedLessons([...completedLessons, currentLesson.id]);
      }
      setCurrentLessonIndex(currentLessonIndex + 1);
      setQuizAnswers({});
      setShowQuizResults(false);
      window.scrollTo(0, 0);
    } else {
      // Complete the course
      if (!completedLessons.includes(currentLesson.id)) {
        setCompletedLessons([...completedLessons, currentLesson.id]);
      }
      setCourseCompleted(true);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(currentLessonIndex - 1);
      setQuizAnswers({});
      setShowQuizResults(false);
      window.scrollTo(0, 0);
    }
  };

  const handleQuizAnswer = (questionIndex: number, answerIndex: number) => {
    setQuizAnswers({
      ...quizAnswers,
      [questionIndex]: answerIndex,
    });
  };

  const handleSubmitQuiz = () => {
    const correctCount = currentLesson.quiz.reduce((count, question, index) => {
      return quizAnswers[index] === question.correctAnswer ? count + 1 : count;
    }, 0);

    setQuizScore(correctCount);
    setShowQuizResults(true);
  };

  const isQuizComplete = Object.keys(quizAnswers).length === currentLesson.quiz.length;
  const quizPercentage = showQuizResults
    ? Math.round((quizScore / currentLesson.quiz.length) * 100)
    : 0;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SchoolIcon sx={{ fontSize: 40, mr: 2, color: 'white' }} />
            <Box>
              <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
                E-Unify Business Glossary Training
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'white' }}>
                Professional Certification Course • 45 minutes • 19 Lessons
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#4caf50',
              },
            }}
          />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
            Lesson {currentLessonIndex + 1} of {lessons.length} • {Math.round(progress)}% Complete
          </Typography>
        </Paper>

        {/* Module Progress */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {currentLesson.moduleName} - Progress
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Current: Lesson {currentLesson.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed: {completedLessons.length} of {lessons.length}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.round((completedLessons.length / lessons.length) * 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(0, 51, 102, 0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                },
              }}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Module {currentLesson.moduleId} of 6
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.round((currentLesson.moduleId / 6) * 100)}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(0, 51, 102, 0.05)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#4caf50',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Course Completion Screen */}
        {courseCompleted && (
          <Card sx={{ mb: 3, bgcolor: '#e8f5e8', border: '2px solid #4caf50' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <EmojiEvents sx={{ fontSize: 40, color: '#ffd700', mr: 2 }} />
              <Typography variant="h3" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                🎉 Course Completed!
              </Typography>
              <Typography variant="h5" gutterBottom sx={{ color: '#4caf50' }}>
                E-Unify Business Glossary Training
              </Typography>
              <Typography variant="h6" gutterBottom>
                Professional Certification Achieved
              </Typography>
              
              <Box sx={{ my: 4, p: 3, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Your Achievement Summary
                </Typography>
                <Typography variant="body1" paragraph>
                  ✅ Completed all {lessons.length} lessons
                </Typography>
                <Typography variant="body1" paragraph>
                  ✅ Total Training Time: 45 minutes
                </Typography>
                <Typography variant="body1" paragraph>
                  ✅ Certification Valid for 12 months
                </Typography>
                <Typography variant="body1">
                  ✅ Ready to contribute to data governance excellence
                </Typography>
              </Box>

              <Typography variant="body1" paragraph sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                Congratulations! You've successfully completed the E-Unify Business Glossary Training 
                and earned your certification. You're now equipped to create high-quality term definitions, 
                collaborate effectively with your team, and contribute to your organization's data governance initiatives.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Download />}
                  sx={{ 
                    bgcolor: 'primary.main',
                    color: '#ffffff',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 2,
                    border: '2px solid #ffffff',
                    '&:hover': { bgcolor: '#001f4d' },
                    '&:focus-visible': {
                      outline: '3px solid #ffffff',
                      outlineOffset: 2,
                    }
                  }}
                  onClick={handleOpenCertificateDialog}
                >
                  🏆 Generate Certificate
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                  onClick={() => {
                    setCourseCompleted(false);
                    setCurrentLessonIndex(0);
                    setCompletedLessons([]);
                    setQuizAnswers({});
                    setShowQuizResults(false);
                  }}
                >
                  📚 Review Training
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Lesson Content */}
        {!courseCompleted && (
          <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Chip label={`Lesson ${currentLesson.id}`} color="primary" size="small" sx={{ mr: 1 }} />
                <Chip label={currentLesson.duration} size="small" />
              </Box>
              {completedLessons.includes(currentLesson.id) && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Completed"
                  color="success"
                  size="small"
                />
              )}
            </Box>

            <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'primary.main' }}>
              {currentLesson.title}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Content Sections */}
            {currentLesson.content.sections.map((section, index) => (
              <Box key={index} sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                  {section.title}
                </Typography>
                {section.content.map((paragraph, pIndex) => (
                  <Typography
                    key={pIndex}
                    variant="body1"
                    paragraph
                    sx={{
                      whiteSpace: 'pre-line',
                      lineHeight: 1.8,
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}

            <Divider sx={{ my: 3 }} />

            {/* Key Takeaways */}
            <Box sx={{ bgcolor: '#e3f2fd', p: 3, borderRadius: 2, mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                ✓ Key Takeaways
              </Typography>
              <List dense>
                {currentLesson.keyTakeaways.map((takeaway, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    </ListItemIcon>
                    <ListItemText primary={takeaway} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Knowledge Check Quiz */}
            <Box sx={{ bgcolor: '#fff3e0', p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                📝 Knowledge Check
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Test your understanding of this lesson
              </Typography>

              {currentLesson.quiz.map((question, qIndex) => (
                <Box key={qIndex} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Question {qIndex + 1}: {question.question}
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={quizAnswers[qIndex] ?? ''}
                      onChange={(e) => handleQuizAnswer(qIndex, parseInt(e.target.value))}
                    >
                      {question.options.map((option, oIndex) => (
                        <FormControlLabel
                          key={oIndex}
                          value={oIndex}
                          control={<Radio />}
                          label={option}
                          disabled={showQuizResults}
                          sx={{
                            bgcolor:
                              showQuizResults && oIndex === question.correctAnswer
                                ? '#c8e6c9'
                                : showQuizResults && quizAnswers[qIndex] === oIndex
                                ? '#ffcdd2'
                                : 'transparent',
                            borderRadius: 1,
                            px: 2,
                            py: 0.5,
                            mb: 1,
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>
              ))}

              {!showQuizResults && (
                <Button
                  variant="contained"
                  onClick={handleSubmitQuiz}
                  disabled={!isQuizComplete}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Submit Quiz
                </Button>
              )}

              {showQuizResults && (
                <Alert
                  severity={quizPercentage >= 70 ? 'success' : 'warning'}
                  icon={quizPercentage >= 70 ? <TrophyIcon /> : undefined}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="h6">
                    Score: {quizScore} / {currentLesson.quiz.length} ({quizPercentage}%)
                  </Typography>
                  <Typography variant="body2">
                    {quizPercentage >= 70
                      ? '🎉 Great job! You can proceed to the next lesson.'
                      : 'Review the lesson content and try again to score 70% or higher.'}
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
        )}

        {/* Navigation */}
        {!courseCompleted && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handlePreviousLesson}
            disabled={currentLessonIndex === 0}
            size="large"
          >
            Previous Lesson
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNextLesson}
            disabled={!showQuizResults || quizPercentage < 70}
            size="large"
            sx={{ bgcolor: 'primary.main' }}
          >
            {currentLessonIndex === lessons.length - 1 ? 'Complete Course' : 'Next Lesson'}
          </Button>
        </Box>
        )}

        {/* Certificate Dialog */}
        <Dialog
          open={showCertificateDialog}
          onClose={handleCloseCertificateDialog}
          maxWidth="md"
          fullWidth
          aria-labelledby={CERT_DIALOG_TITLE_ID}
          aria-describedby={CERT_DIALOG_DESCRIPTION_ID}
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              bgcolor: '#f5f5f5',
              '&:focus-visible': {
                outline: '3px solid #003366',
                outlineOffset: 4,
              },
            },
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              handleCloseCertificateDialog();
            }
          }}
        >
          <DialogTitle 
            id={CERT_DIALOG_TITLE_ID} 
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pr: 6,
            }}
          >
            <Typography component="span" variant="h6">Generate Certificate of Completion</Typography>
            <IconButton onClick={handleCloseCertificateDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }} dividers>
            <Typography
              id={CERT_DIALOG_DESCRIPTION_ID}
              sx={{ ...visuallyHidden, mb: 0 }}
            >
              Enter your full name to generate, preview, and download a personalized certificate of completion for the Business Glossary Training program.
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Provide your full name exactly as you want it to appear. The preview updates live and is optimized for screen readers, printing, and PDF export.
              </Typography>
              <TextField
                fullWidth
                label="Full Name"
                id={certificateNameFieldId}
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                placeholder="Jane Q. Doe"
                sx={{ mb: 1.5 }}
                aria-describedby={certificateNameHelperId}
                autoComplete="name"
                required
                inputProps={{ 'aria-required': 'true' }}
              />
              <Typography id={certificateNameHelperId} variant="body2" sx={{ ...visuallyHidden }}>
                Full name is required to personalize your certificate.
              </Typography>
            </Box>

            {studentName && (
              <Box
                id={CERTIFICATE_CONTENT_ID}
                role="document"
                aria-label={`Certificate of completion awarded to ${studentName}`}
                sx={{
                  position: 'relative',
                  p: { xs: 4, md: 6 },
                  bgcolor: 'white',
                  borderRadius: 4,
                  border: '6px solid #003366',
                  textAlign: 'center',
                  maxWidth: 1000,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 4, md: 5 },
                  color: '#1a1a1a',
                  overflow: 'hidden',
                  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)',
                  backgroundImage:
                    'linear-gradient(135deg, rgba(0,51,102,0.08) 25%, transparent 25%), linear-gradient(135deg, transparent 75%, rgba(0,51,102,0.08) 75%)',
                  backgroundSize: '32px 32px',
                  backgroundPosition: '0 0, 16px 16px',
                  '@media print': {
                    maxWidth: '100%',
                    height: '100%',
                    minHeight: 'auto',
                    padding: '1in',
                    gap: '1.5rem',
                    justifyContent: 'space-between',
                    borderRadius: 0,
                    borderWidth: '8px',
                    boxShadow: 'none',
                    background: 'white',
                    display: 'block', // Change from flex to block for better print control
                    pageBreakInside: 'avoid',
                  },
                }}
              >
                {/* Decorative Border */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 24,
                    border: '3px solid rgba(0, 51, 102, 0.4)',
                    borderRadius: 3,
                    pointerEvents: 'none',
                    '@media print': {
                      borderWidth: '4px',
                      inset: '0.5in',
                    },
                  }}
                  aria-hidden="true"
                />
                {/* Watermark */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(0,51,102,0.06), transparent 60%), radial-gradient(circle at 75% 75%, rgba(179,27,27,0.04), transparent 60%)',
                    pointerEvents: 'none',
                    '@media print': {
                      display: 'none',
                    },
                  }}
                  aria-hidden="true"
                />

                {/* Logo Section - Separate from header to prevent overlap */}
                <Box sx={{ 
                  textAlign: 'center', 
                  mb: { xs: 3, md: 4 },
                  '@media print': {
                    marginBottom: '1rem',
                  }
                }}>
                  <img
                    src="/images/eunify-m.png"
                    alt="E-Unify"
                    style={{
                      width: '180px',
                      height: '60px',
                      objectFit: 'contain',
                      display: 'inline-block',
                    }}
                  />
                </Box>

                {/* Header Section */}
                <Box sx={{ mb: { xs: 4, md: 5 } }}>
                  <Typography
                    id="certificate-heading"
                    variant="h2"
                    component="h1"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 700,
                      fontFamily: 'Merriweather, Georgia, serif',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      mb: { xs: 2, md: 3 },
                      fontSize: { xs: '2.2rem', md: '3rem' },
                      lineHeight: 1.1,
                      textAlign: 'center',
                    }}
                  >
                    Certificate of Completion
                  </Typography>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      color: 'secondary.main',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      mb: 3,
                      fontSize: { xs: '1.4rem', md: '1.8rem' },
                      textAlign: 'center',
                    }}
                  >
                    E-Unify Business Glossary Training
                  </Typography>
                </Box>

                {/* Recipient Section */}
                <Box sx={{ position: 'relative', mb: { xs: 4, md: 5 } }}>
                  <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', fontSize: '1.1rem', mb: 3 }}>
                    This certifies that
                  </Typography>
                  <Typography
                    variant="h3"
                    component="p"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 700,
                      fontFamily: 'Merriweather, Georgia, serif',
                      textDecoration: 'underline',
                      textDecorationColor: '#B31B1B',
                      textDecorationThickness: '3px',
                      textUnderlineOffset: '8px',
                      mb: { xs: 3, md: 4 },
                      display: 'inline-block',
                      px: { xs: 3, md: 4 },
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {studentName}
                  </Typography>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{ maxWidth: 650, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}
                  >
                    has successfully completed the comprehensive Business Glossary Training curriculum, demonstrating proficiency in glossary governance, terminology alignment, and data stewardship excellence within the E-Unify platform.
                  </Typography>
                </Box>

                {/* Signature Section */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mt: 'auto',
                  pt: 4,
                  borderTop: '2px solid #003366',
                  '@media print': {
                    pt: '2rem',
                  }
                }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {/* Handwritten signature style */}
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: 'cursive',
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        color: 'primary.main',
                        fontWeight: 400,
                        mb: 1,
                        lineHeight: 1,
                        letterSpacing: '0.05em',
                      }}
                    >
                      Russell B Riehl
                    </Typography>
                    <Box sx={{ borderBottom: '1px solid #666', width: '200px', mx: 'auto', mb: 1 }} aria-hidden="true" />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', mt: 1 }}>
                      Program Director
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', fontSize: '0.9rem', mt: 2 }}>
                    <strong>Certificate ID:</strong> {displayedSerialNumber} - <strong>Date Issued:</strong> {displayedIssueDate} - <strong>Valid Through:</strong> {displayedExpiryDate}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              mt: 4,
              pt: 3,
              borderTop: '2px solid #e0e0e0'
            }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Print />}
                onClick={handleCertificatePrint}
                disabled={!studentName}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&:disabled': {
                    bgcolor: '#cccccc',
                    color: '#666666'
                  },
                  minWidth: '240px',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                💾 Download Certificate
              </Button>
            </Box>

            {!studentName && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please enter your full name above to enable certificate download options.
              </Alert>
            )}
          </DialogContent>
        </Dialog>

      </Container>
    </Box>
  );
};

export default BusinessGlossaryTraining;
